import { useState, useEffect } from "react";

// Material Dashboard 2 PRO React contexts
import { useMaterialUIController } from "context";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

import MDBox from "../../../components/MDBox";
import MDModal from "../../../components/MDModal";
import MDTypography from "../../../components/MDTypography";
import MDButton from "../../../components/MDButton";

import { putAbsence, postAbsence, getAbsences, deleteAbsence } from "../../../api/absences";

import Absence from "./Absence";
import moment from "moment";

import PropTypes from "prop-types";

export default function CreateEditAbsences({
  edit = false,
  absence = null,
  setAbsences,
  userId = null,
  global = false,
  editOpen,
  setEditOpen,
  renderStep = null,
}) {
  const [open, setOpen] = useState(false);

  const [absenceId, setAbsenceId] = useState(null);
  const [absenceDescription, setAbsenceDescription] = useState("");
  const [tripArea, setTripArea] = useState("");
  const [absenceStartDate, setAbsenceStartDate] = useState(null);
  const [absenceEndDate, setAbsenceEndDate] = useState(null);

  useEffect(() => {
    if (edit && absence) {
      setOpen(editOpen);
      setAbsenceId(absence.absence_id);
      setAbsenceDescription(absence.absence_description);
      setTripArea(absence.trip_area);
      setAbsenceStartDate(absence.absence_start_date);
      setAbsenceEndDate(absence.absence_end_date);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [absence]);

  useEffect(() => {
    setOpen(editOpen);
  }, [editOpen]);

  async function createAbsenceAPI() {
    const absence = {
      absence_user_id: userId.id,
      absence_description: absenceDescription,
      ...(tripArea && { trip_area: tripArea }),
      absence_start_date: absenceStartDate ? moment(absenceStartDate).format("YYYY-MM-DD") : null,
      absence_end_date: absenceEndDate ? moment(absenceEndDate).format("YYYY-MM-DD") : null,
    };
    await postAbsence(absence);
  }

  async function deleteAbsenceAPI() {
    await deleteAbsence(absenceId);
  }

  async function updateAbsenceAPI() {
    const absence = {
      absence_id: absenceId,
      absence_description: absenceDescription,
      trip_area: tripArea,
      absence_start_date: absenceStartDate ? moment(absenceStartDate).format("YYYY-MM-DD") : null,
      absence_end_date: absenceEndDate ? moment(absenceEndDate).format("YYYY-MM-DD") : null,
    };
    await putAbsence(absence, absenceId);
  }

  async function fetchAbsences() {
    const response = await getAbsences(global ? "none" : userId.id);
    setAbsences(response.data);
  }

  function resetData() {
    setAbsenceId(null);
    setTripArea("");
    setAbsenceDescription("");
    setAbsenceStartDate(null);
    setAbsenceEndDate(null);
  }

  async function deleteAbs() {
    await deleteAbsenceAPI();
    await fetchAbsences();
    exit();
  }

  async function submit() {
    if (edit) {
      await updateAbsenceAPI();
      await fetchAbsences();
      resetData();
    } else {
      await createAbsenceAPI();
      await fetchAbsences();
      resetData();
    }
    exit();
  }

  async function cancel() {
    if (edit) {
      await fetchAbsences();
    } else {
      resetData();
    }
    exit();
  }

  function exit() {
    setOpen(false);
    if (edit) setEditOpen(false);
  }

  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  return (
    <>
      {!edit && (
        <MDButton
          variant="contained"
          style={{ height: 53.13, width: "100%" }}
          color={darkMode ? "white" : "dark"}
          size="large"
          onClick={() => setOpen(true)}
        >
          Ajouter une absence
        </MDButton>
      )}
      <MDModal open={open} onClose={() => exit()}>
        <MDBox mt={25} mb={9}>
          <Grid container justifyContent="center">
            <Grid item xs={12} lg={8}>
              <Card>
                <MDBox
                  mt={-5}
                  mb={2}
                  mx={2}
                  variant="gradient"
                  sx={{
                    background: darkMode ? "white" : "linear-gradient(195deg, #364c70, #273956)",
                    boxShadow: darkMode ? "0 1px 6px 1px white" : "0 1px 6px 1px #344767",
                  }}
                  borderRadius="lg"
                  textAlign="center"
                >
                  <MDBox mt={2} mb={2}>
                    <MDBox>
                      {edit ? (
                        <>
                          <MDTypography
                            variant="h3"
                            fontWeight="bold"
                            color={darkMode ? "inherit" : "white"}
                          >
                            Modification de l'absence
                          </MDTypography>
                          <MDTypography variant="h5" fontWeight="regular" color="light">
                            Veuillez rentrer les informations relatives à l'absence
                          </MDTypography>
                        </>
                      ) : (
                        <>
                          <MDTypography
                            variant="h3"
                            fontWeight="bold"
                            color={darkMode ? "inherit" : "white"}
                          >
                            Ajouter d'une nouvelle absence
                          </MDTypography>
                          <MDTypography
                            variant="h5"
                            fontWeight="regular"
                            color={darkMode ? "inherit" : "white"}
                          >
                            Veuillez rentrer les informations relatives à l'absence
                          </MDTypography>
                        </>
                      )}
                    </MDBox>
                  </MDBox>
                </MDBox>
                <MDBox p={2}>
                  <MDBox>
                    <Absence
                      absenceDescription={absenceDescription}
                      setAbsenceDescription={setAbsenceDescription}
                      tripArea={tripArea}
                      setTripArea={setTripArea}
                      absenceStartDate={absenceStartDate}
                      setAbsenceStartDate={setAbsenceStartDate}
                      absenceEndDate={absenceEndDate}
                      setAbsenceEndDate={setAbsenceEndDate}
                    />
                    <MDBox mt={3} width="100%" display="block" justifyContent="space-between">
                      <MDBox textAlign="right">
                        <Grid container justifyContent="right" spacing={1.5}>
                          <Grid item>
                            <MDButton variant="gradient" color="warning" onClick={cancel}>
                              Annuler
                            </MDButton>
                          </Grid>
                          <Grid item>
                            <MDButton
                              disabled={
                                !absenceEndDate || absenceDescription === "" || !absenceStartDate
                              }
                              variant="gradient"
                              color="success"
                              onClick={submit}
                            >
                              Envoyer
                            </MDButton>
                          </Grid>
                          {edit && (
                            <Grid item>
                              <MDButton variant="gradient" color="error" onClick={deleteAbs}>
                                Supprimer l'absence
                              </MDButton>
                            </Grid>
                          )}
                        </Grid>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      </MDModal>
    </>
  );
}

CreateEditAbsences.propTypes = {
  edit: PropTypes.bool.isRequired,
};
